/**
 * Filters Use Cases Factory
 *
 * Assembles all filter-related use cases with their dependencies.
 *
 * @module @reports/composition/factories/createFiltersUseCases
 */

import type { AxiosInstance } from 'axios';
import type { AuthService } from 'types/AuthService';
import type { FiltersUseCases } from '../types';

// Infrastructure imports
import { createReportsHttpClient } from '@reports/infrastructure/api';
import { createReportApiMapper } from '@reports/infrastructure/mappers';
import { createReportsApiRepository, createReportStorageRepository } from '@reports/infrastructure/repositories';

// Use cases imports
import {
  fetchFilterCatalogUseCase,
  saveAppliedFiltersUseCase,
  getAppliedFiltersUseCase,
  clearAppliedFiltersUseCase,
  getFiltersMetadataUseCase,
  saveFiltersMetadataUseCase,
  clearFiltersMetadataUseCase,
} from '@reports/application/use-cases';

/**
 * Creates all filter use cases with injected dependencies.
 */
export const createFiltersUseCases = (httpClient: AxiosInstance, authService: AuthService): FiltersUseCases => {
  // API Port for fetchFilterCatalog
  const reportsHttpClient = createReportsHttpClient({ httpClient, authService });
  const mapper = createReportApiMapper();
  const apiPort = createReportsApiRepository({
    httpClient: reportsHttpClient,
    mapper,
  });

  // Storage Port for save/get/clear operations
  const storagePort = createReportStorageRepository();

  // Compose use cases
  return {
    fetchFilterCatalog: fetchFilterCatalogUseCase(apiPort),
    saveAppliedFilters: saveAppliedFiltersUseCase(storagePort),
    getAppliedFilters: getAppliedFiltersUseCase(storagePort),
    clearAppliedFilters: clearAppliedFiltersUseCase(storagePort),
    getFiltersMetadata: getFiltersMetadataUseCase(storagePort),
    saveFiltersMetadata: saveFiltersMetadataUseCase(storagePort),
    clearFiltersMetadata: clearFiltersMetadataUseCase(storagePort),
  };
};
